import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseMidi, type Midi } from "@midi-fantom-utils/midi-core";
import {
  buildPlaybackData,
  buildReport,
  countNotes,
  measureBoundaries,
  toFantomName,
  toMultiTrackFormat,
  type Pattern,
  type PatternId,
  type Slice,
  type Report,
} from "@midi-fantom-utils/midi-util";
import type { SoundfontSource } from "@midi-fantom-utils/player-core";
import { FilePicker } from "./components/FilePicker.js";
import { ErrorBanner } from "./components/ErrorBanner.js";
import { SettingsModal } from "./components/SettingsModal.js";
import { HeaderInfo } from "./components/HeaderInfo.js";
import { TimelinePlayer } from "./components/TimelinePlayer.js";
import { PianoRoll, type PianoRollMarker } from "./components/PianoRoll.js";
import { ChannelList, type ChannelListEntry } from "./components/ChannelList.js";
import { SliceBar } from "./components/SliceBar.js";
import { PatternDialog, type ChannelNoteCount } from "./components/PatternDialog.js";
import { PatternScreen } from "./components/PatternScreen.js";
import { PatternEditor } from "./components/PatternEditor.js";
import { ExportBar } from "./components/ExportBar.js";
import { useCatalog } from "./useCatalog.js";
import { usePatternStore } from "./patterns/store.js";
import { applyChannelPrograms, channelProgram } from "./channelInstruments.js";
import { useProgramMap } from "./useProgramMap.js";
import {
  loadSettings,
  saveSettings,
  defaultPianoRollColors,
  APP_VERSION,
  IS_DEV,
  type PianoRollColors,
  type Settings,
} from "./config.js";

type AppState =
  | { kind: "idle" }
  | { kind: "parsing"; file: File }
  | {
      kind: "ready";
      file: File;
      sourceMidi: Midi;
      sourceReport: Report;
    }
  | { kind: "error"; message: string };

const stripExt = (name: string): string => {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(0, idx) : name;
};

// Первая метка всегда в M1, не удаляется. id зафиксирован как "m0".
const FIRST_MARKER: PianoRollMarker = { id: "m0", measure: 1, name: "Начало" };

export const App = () => {
  const [state, setState] = useState<AppState>({ kind: "idle" });
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [editingPatternId, setEditingPatternId] = useState<PatternId | null>(null);
  const [tick, setTick] = useState(0);
  // Каналы, которые показываются в piano roll (сессия). Сбрасывается на
  // новом файле на все использованные каналы.
  const [enabledChannels, setEnabledChannels] = useState<ReadonlySet<number>>(new Set());
  const [channelAccordionOpen, setChannelAccordionOpen] = useState(false);
  // Переопределения инструментов каналов: channel → GM program 0..127.
  // Сбрасывается на новом файле (см. handleFile/reset).
  const [instrumentOverrides, setInstrumentOverrides] = useState<ReadonlyMap<number, number>>(
    new Map()
  );
  // Предупреждение, что подготовленные срезы могут быть неактуальными после
  // смены инструмента канала. Снимается повторным «Подготовить срезы».
  const [slicesStale, setSlicesStale] = useState(false);
  // Метки над таймлайном (по-файлу).
  const [markers, setMarkers] = useState<PianoRollMarker[]>([]);
  const nextMarkerIdRef = useRef(1);
  // Срез, из которого открыт PatternDialog (клик по срезу в SliceBar).
  const [bindingSlice, setBindingSlice] = useState<Slice | null>(null);

  const skipFirstPersist = useRef(true);
  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    saveSettings(settings);
  }, [settings]);

  const catalog = useCatalog();
  const patternStore = usePatternStore();

  const resetChannels = useCallback((midi: Midi) => {
    const used = new Set<number>();
    for (const t of midi.tracks) {
      for (const n of t.notes) used.add(n.channel ?? t.channel);
    }
    setEnabledChannels(used);
  }, []);

  const resetMarkers = useCallback(() => {
    setMarkers([FIRST_MARKER]);
    nextMarkerIdRef.current = 1;
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      setState({ kind: "parsing", file });
      try {
        const buffer = await file.arrayBuffer();
        const sourceMidi = parseMidi(new Uint8Array(buffer));
        const sourceReport = buildReport(sourceMidi, file.name);
        setState({
          kind: "ready",
          file,
          sourceMidi,
          sourceReport,
        });
        patternStore.reset();
        setInstrumentOverrides(new Map());
        setSlicesStale(false);
        resetChannels(sourceMidi);
        resetMarkers();
      } catch (err) {
        setState({
          kind: "error",
          message: `Не удалось разобрать файл "${file.name}": ${
            (err as Error).message ?? err
          }`,
        });
      }
    },
    [patternStore, resetChannels, resetMarkers]
  );

  const reset = useCallback(() => {
    setState({ kind: "idle" });
    setDownloadError(null);
    patternStore.reset();
    setInstrumentOverrides(new Map());
    setSlicesStale(false);
    setMarkers([FIRST_MARKER]);
    nextMarkerIdRef.current = 1;
  }, [patternStore]);

  const sourceMidi = state.kind === "ready" ? state.sourceMidi : null;

  // Каналы и число нот (для чипов-переключателей и PatternDialog).
  const channelCounts = useMemo<ChannelNoteCount[]>(() => {
    if (!sourceMidi) return [];
    const counts = new Map<number, number>();
    for (const t of sourceMidi.tracks) {
      for (const n of t.notes) {
        const ch = n.channel ?? t.channel;
        counts.set(ch, (counts.get(ch) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([channel, noteCount]) => ({ channel, noteCount }))
      .sort((a, b) => a.channel - b.channel);
  }, [sourceMidi]);

  const boundaries = useMemo<number[]>(() => {
    if (!sourceMidi) return [];
    let maxEnd = 0;
    for (const t of sourceMidi.tracks) {
      for (const n of t.notes) maxEnd = Math.max(maxEnd, n.ticks + n.durationTicks);
    }
    const endTick = Math.max(maxEnd, sourceMidi.header.ppq * 4);
    return measureBoundaries(
      sourceMidi.header.ppq,
      sourceMidi.header.timeSignatures,
      endTick
    );
  }, [sourceMidi]);

  // MIDI для piano roll — всегда per-channel раскладка (marker-воркфлоу).
  const pianoRollMidi = useMemo<Midi | null>(() => {
    if (!sourceMidi) return null;
    return toMultiTrackFormat(sourceMidi);
  }, [sourceMidi]);

  // Единая точка правды для downstream: piano roll, «Прослушать», PatternDialog.
  // `applyChannelPrograms` — no-op (ref-равенство) при пустых overrides.
  const effectiveMidi = useMemo<Midi | null>(() => {
    if (!pianoRollMidi) return null;
    return applyChannelPrograms(pianoRollMidi, instrumentOverrides);
  }, [pianoRollMidi, instrumentOverrides]);

  const sourcePlayback = useMemo<ReturnType<typeof buildPlaybackData> | null>(() => {
    if (!effectiveMidi) return null;
    return buildPlaybackData(effectiveMidi);
  }, [effectiveMidi]);

  // Список каналов 0..15 для аккордиона «Каналы» (used/active/noteCount).
  const channelListEntries = useMemo<ChannelListEntry[]>(() => {
    const counts = new Map<number, number>();
    if (pianoRollMidi) {
      for (const t of pianoRollMidi.tracks) {
        for (const n of t.notes) {
          const ch = n.channel ?? t.channel;
          counts.set(ch, (counts.get(ch) ?? 0) + 1);
        }
      }
    }
    return Array.from({ length: 16 }, (_, i) => i).map((channel) => ({
      channel,
      used: (counts.get(channel) ?? 0) > 0,
      active: enabledChannels.has(channel),
      noteCount: counts.get(channel) ?? 0,
    }));
  }, [pianoRollMidi, enabledChannels]);

  // GM-программа на канал из effectiveMidi (для селекторов инструментов).
  const channelPrograms = useMemo<ReadonlyMap<number, number>>(() => {
    const map = new Map<number, number>();
    if (effectiveMidi) {
      for (const channel of Array.from({ length: 16 }, (_, i) => i)) {
        map.set(channel, channelProgram(effectiveMidi, channel));
      }
    }
    return map;
  }, [effectiveMidi]);

  // Смена инструмента канала: копируем override и помечаем срезы «устаревшими»,
  // если они уже подготовлены.
  const setInstrument = useCallback(
    (channel: number, program: number) => {
      setInstrumentOverrides((prev) => {
        const next = new Map(prev);
        next.set(channel, program);
        return next;
      });
      if (patternStore.state.slices.length > 0) setSlicesStale(true);
    },
    [patternStore.state.slices.length]
  );

  const programMap = useProgramMap(settings.soundfont, channelAccordionOpen);


  const fileBaseName = state.kind === "ready" ? stripExt(toFantomName(state.file.name)) : "";

  const markerMeasureAtTick = useCallback(
    (tick: number): number => {
      let idx = 0;
      for (let i = boundaries.length - 1; i >= 0; i -= 1) {
        if (boundaries[i]! <= tick) {
          idx = i;
          break;
        }
      }
      // Мера всегда в [1, boundaries.length - 1]: клик на/после конца файла
      // не должен давать меру `boundaries.length` (нулевой срез).
      return Math.min(idx + 1, Math.max(1, boundaries.length - 1));
    },
    [boundaries]
  );

  const addMarker = useCallback(
    (tick: number) => {
      const measure = markerMeasureAtTick(tick);
      if (markers.some((m) => m.measure === measure)) return;
      const marker: PianoRollMarker = {
        id: `m${nextMarkerIdRef.current++}`,
        measure,
        name: "",
      };
      setMarkers((prev) =>
        [...prev, marker].sort((a, b) => a.measure - b.measure)
      );
    },
    [markerMeasureAtTick, markers]
  );

  const moveMarker = useCallback(
    (id: string, tick: number) => {
      const measure = markerMeasureAtTick(tick);
      setMarkers((prev) => {
        // Не ставим на меру, уже занятую другой меткой (иначе дубль).
        if (prev.some((m) => m.id !== id && m.measure === measure)) return prev;
        return prev
          .map((m) => (m.id === id ? { ...m, measure } : m))
          .sort((a, b) => a.measure - b.measure);
      });
    },
    [markerMeasureAtTick]
  );

  const renameMarker = useCallback((id: string, name: string) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, name } : m)));
  }, []);

  const deleteMarker = useCallback((id: string) => {
    // Первую метку нельзя удалить (всегда остаётся хотя бы одна), но её
    // можно переместить. Остальные метки удаляются свободно.
    setMarkers((prev) =>
      prev.filter((m) => m.id !== id || m.id === FIRST_MARKER.id)
    );
  }, []);

  const toggleChannel = useCallback((ch: number) => {
    setEnabledChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  }, []);

  // «Подготовить срезы»: сегмент × включённый канал. Повторный клик
  // пересоздаёт (сначала очищаем существующие срезы).
  const prepareSlices = useCallback(() => {
    if (markers.length === 0 || !sourceMidi) return;
    const sorted = [...markers].sort((a, b) => a.measure - b.measure);
    const endMeasure = boundaries.length;
    for (const s of patternStore.state.slices) patternStore.removeSlice(s.id);
    for (let i = 0; i < sorted.length; i += 1) {
      const startMeasure = sorted[i]!.measure;
      const nextMeasure = sorted[i + 1]?.measure ?? endMeasure;
      if (nextMeasure <= startMeasure) continue; // нулевой сегмент не создаём
      for (const ch of enabledChannels) {
        patternStore.addSlice({ startMeasure, endMeasure: nextMeasure, channel: ch });
      }
    }
    setSlicesStale(false);
  }, [boundaries.length, enabledChannels, markers, patternStore, sourceMidi]);

  const handleCreatePattern = useCallback(
    (pattern: Pattern) => {
      patternStore.upsertPattern(pattern);
      if (bindingSlice) {
        patternStore.setSlicePattern(bindingSlice.id, pattern.id);
      }
    },
    [patternStore, bindingSlice]
  );

  const editingPattern = useMemo<Pattern | null>(() => {
    if (!editingPatternId) return null;
    return patternStore.state.patterns.get(editingPatternId) ?? null;
  }, [editingPatternId, patternStore.state.patterns]);

  // Префилл имени для PatternDialog из метки-начала сегмента.
  const bindingDefaultName = useMemo<string | undefined>(() => {
    if (!bindingSlice) return undefined;
    const marker = markers.find((m) => m.measure === bindingSlice.range.startMeasure);
    if (!marker) return undefined;
    const chLabel = bindingSlice.range.channel === 9 ? "9 Drums" : `ch ${bindingSlice.range.channel}`;
    return marker.name ? `${marker.name} · ${chLabel}` : chLabel;
  }, [bindingSlice, markers]);

  // Цвета piano roll: передаём проп `colors` ТОЛЬКО когда пользователь их
  // кастомизировал. Иначе (дефолтные цвета) `getColors` берёт тему из
  // CSS-переменных (--piano-key-* / --grid-*), которые адаптируются под
  // prefers-color-scheme. Передача дефолтных настроек всегда заменяла бы тему
  // фиксированными тёмными значениями и делала подписи/сетку невидимыми
  // в светлой теме.
  const pianoRollColors = useMemo<PianoRollColors | undefined>(() => {
    const c = settings.appearance.pianoRoll.colors;
    const d = defaultPianoRollColors();
    const keys = Object.keys(d) as ReadonlyArray<keyof PianoRollColors>;
    const customized = keys.some((k) => c[k] !== d[k]);
    return customized ? c : undefined;
  }, [settings.appearance.pianoRoll.colors]);
  const pianoRollHeight = settings.appearance.pianoRoll.height;

  return (
    <div className="app">
      <header className="app__header">
        <h1>Roland Fantom 06/07/08 MIDI tools</h1>
        <p>Подготовка MIDI-файлов для Roland Fantom 06/07/08 (Pattern workflow)</p>
        <span
          className={`app__version${IS_DEV ? " app__version--dev" : ""}`}
          title="Версия сборки"
        >
          v{APP_VERSION}
        </span>
        <button
          type="button"
          className="gear-icon"
          onClick={() => setSettingsOpen(true)}
          aria-label="Открыть настройки"
          title="Настройки"
        >
          ⚙
        </button>
      </header>

      <main className="app__main">
        {state.kind === "idle" || state.kind === "parsing" ? (
          <FilePicker
            onFile={handleFile}
            disabled={state.kind === "parsing"}
            currentName={state.kind === "parsing" ? state.file.name : undefined}
          />
        ) : null}

        {state.kind === "error" ? (
          <ErrorBanner message={state.message} onDismiss={reset} />
        ) : null}

        {downloadError ? (
          <div className="banner banner--error" role="alert">
            <span>{downloadError}</span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setDownloadError(null)}
            >
              Закрыть
            </button>
          </div>
        ) : null}

        {state.kind === "ready" && sourceMidi && pianoRollMidi ? (
          <>
            <section className="card">
              <div className="app__results-head">
                <div>
                  <strong>{state.file.name}</strong>
                </div>
                <div className="app__results-actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={reset}
                  >
                    Другой файл
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setHeaderCollapsed((v) => !v)}
                  >
                    {headerCollapsed ? "▾ Показать заголовок" : "▸ Скрыть заголовок"}
                  </button>
                </div>
              </div>
              {headerCollapsed ? null : (
                <HeaderInfo
                  title="Исходный файл"
                  kind="source"
                  fileName={state.sourceReport.fileName}
                  format={state.sourceMidi.format}
                  trackCount={state.sourceMidi.tracks.length}
                  durationLabel={state.sourceReport.duration}
                  durationTicks={state.sourceReport.durationTicks}
                  tempos={state.sourceReport.tempos}
                  timeSignatures={state.sourceReport.timeSignatures}
                  keySignatures={state.sourceReport.keySignatures}
                  detectedKey={state.sourceReport.detectedKey}
                />
              )}
            </section>

            <section className="card">
              <TimelinePlayer
                midi={state.sourceMidi}
                playback={sourcePlayback!}
                fileName={state.file.name}
                source={settings.soundfont}
                onOpenSettings={() => setSettingsOpen(true)}
                onTickChange={setTick}
              />
            </section>

            <section className="card">
              <h2 className="card__title">Таймлайн (piano roll)</h2>
              <ChannelList
                open={channelAccordionOpen}
                onToggleOpen={() => setChannelAccordionOpen((v) => !v)}
                channels={channelListEntries}
                programs={channelPrograms}
                programMap={programMap}
                onToggleChannel={toggleChannel}
                onInstrumentChange={setInstrument}
              />
              {slicesStale ? (
                <div className="banner banner--error" role="alert">
                  <span>
                    Инструменты изменены — подготовленные срезы могут быть
                    неактуальными. Нажмите «Подготовить срезы» повторно.
                  </span>
                </div>
              ) : null}
              <PianoRoll
                midi={effectiveMidi!}
                width={1200}
                height={pianoRollHeight}
                mode="view"
                playheadTick={tick}
                onSeek={setTick}
                enabledChannels={enabledChannels}
                colors={pianoRollColors}
                markers={markers}
                onAddMarker={addMarker}
                onMoveMarker={moveMarker}
                onRenameMarker={renameMarker}
                onDeleteMarker={deleteMarker}
              />
              <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={prepareSlices}
                >
                  Подготовить срезы
                </button>
              </div>
            </section>

            <section className="card">
              <h2 className="card__title">Срезы</h2>
              <SliceBar
                slices={patternStore.state.slices}
                patterns={patternStore.state.patterns}
                onBind={setBindingSlice}
                onEdit={(sliceId) => {
                  const slice = patternStore.state.slices.find((s) => s.id === sliceId);
                  if (slice?.patternId) setEditingPatternId(slice.patternId);
                }}
                onDelete={(id) => patternStore.removeSlice(id)}
              />
            </section>

            <section className="card">
              <h2 className="card__title">Паттерны</h2>
              <PatternScreen
                patterns={patternStore.state.patterns}
                source={settings.soundfont}
                onEdit={(id) => setEditingPatternId(id)}
                onDelete={(id) => patternStore.deletePattern(id)}
              />
              <div style={{ marginTop: 12 }}>
                <ExportBar
                  patterns={[...patternStore.state.patterns.values()]}
                  projectName={fileBaseName}
                  onError={setDownloadError}
                />
              </div>
            </section>
          </>
        ) : null}
      </main>

      <SettingsModal
        open={settingsOpen}
        source={settings.soundfont}
        onSourceChange={(next: SoundfontSource) =>
          setSettings((s) => ({ ...s, soundfont: next }))
        }
        catalog={catalog}
        pianoRollAppearance={settings.appearance.pianoRoll}
        onAppearanceChange={(next) =>
          setSettings((s) => ({ ...s, appearance: { pianoRoll: next } }))
        }
        onClose={() => setSettingsOpen(false)}
      />

      {state.kind === "ready" && sourceMidi && bindingSlice ? (
        <PatternDialog
          open
          source={effectiveMidi!}
          range={bindingSlice.range}
          fileBaseName={fileBaseName}
          existing={patternStore.state.patterns}
          channelCounts={channelCounts}
          defaultName={bindingDefaultName}
          onClose={() => setBindingSlice(null)}
          onCreate={(pat) => {
            handleCreatePattern(pat);
            setBindingSlice(null);
          }}
        />
      ) : null}

      {editingPattern ? (
        <PatternEditor
          open
          pattern={editingPattern}
          onSave={(midi) => {
            patternStore.upsertPattern({ ...editingPattern, midi, noteCount: countNotes(midi) });
          }}
          onClose={() => setEditingPatternId(null)}
        />
      ) : null}
    </div>
  );
};
